/**
 * Для YouTube thumbnail URL возвращает URL максимального качества (maxresdefault).
 * Поддерживает оба домена: img.youtube.com и i.ytimg.com.
 * Если переданный URL уже maxresdefault или не YouTube — возвращает как есть.
 */
export const getYouTubeBestThumbnailUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  const u = url.trim();
  const isImgYoutube = u.includes('img.youtube.com/vi/');
  const isYtimg = u.includes('i.ytimg.com/vi/');
  if ((isImgYoutube || isYtimg) && (u.includes('/hqdefault') || u.includes('/sddefault'))) {
    const match = u.match(/\/vi\/([^/]+)/);
    if (match && match[1]) {
      const base = isYtimg ? 'https://i.ytimg.com' : 'https://img.youtube.com';
      return `${base}/vi/${match[1]}/maxresdefault.jpg`;
    }
  }
  return u;
};

// Размеры изображений для разных контекстов
export const IMAGE_SIZES = {
  // Аватары
  AVATAR_THUMBNAIL: 40,    // Маленькие аватары в карточках
  AVATAR_SMALL: 64,        // Аватары в списках
  AVATAR_MEDIUM: 128,      // Аватары в профилях
  AVATAR_LARGE: 256,       // Большие аватары
  
  // Обложки маршрутов/видео
  COVER_THUMBNAIL: 320,    // Миниатюры в карточках (лента, карта)
  COVER_SMALL: 640,        // Маленькие обложки
  COVER_MEDIUM: 1280,      // Средние обложки (детальная страница)
  COVER_LARGE: 1920,       // Большие обложки (полноэкранный просмотр)
};

/**
 * Проверяет поддержку WebP формата
 * @returns {boolean} - true если браузер поддерживает WebP
 */
const supportsWebP = () => {
  if (typeof window === 'undefined') return false;
  
  // Кешируем результат проверки
  if (typeof supportsWebP.cached !== 'undefined') {
    return supportsWebP.cached;
  }
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const result = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  supportsWebP.cached = result;
  return result;
};

/**
 * Определяет оптимальное качество в зависимости от устройства и контекста
 * @param {boolean} isDesktop - Является ли устройство десктопом
 * @param {string} context - Контекст использования ('fullscreen', 'detail', 'card', 'thumbnail')
 * @returns {number} - Качество (1-100)
 */
const getOptimalQuality = (isDesktop = false, context = 'card') => {
  // Для десктопа используем более высокое качество, особенно для слайдшоу
  if (isDesktop) {
    if (context === 'fullscreen' || context === 'slideshow') {
      return 100; // Максимальное качество для слайдшоу на десктопе
    }
    if (context === 'detail') {
      return 100; // Максимальное качество для детальных страниц на десктопе (было 90)
    }
    return 85; // Стандартное качество для карточек
  }
  
  // Для мобильных устройств используем более низкое качество для экономии трафика
  if (context === 'fullscreen' || context === 'slideshow') {
    return 60; // Снижено с 70 до 60 для слайдшоу на мобильных (оптимизация PageSpeed)
  }
  if (context === 'detail') {
    return 70; // Снижено с 75 до 70 для детальных страниц на мобильных
  }
  return 65; // Снижено с 70 до 65 для карточек на мобильных
};

/**
 * Генерирует оптимизированный URL изображения с параметрами размера
 * Поддерживает WebP формат с fallback на оригинальный формат
 * 
 * @param {string} originalUrl - Оригинальный URL изображения
 * @param {number|string} size - Размер изображения (width в пикселях или 'thumbnail', 'small', 'medium', 'large')
 * @param {number} quality - Качество (1-100, по умолчанию auto - определяется автоматически)
 * @param {string} format - Формат изображения ('webp', 'auto', или null для оригинального)
 * @param {boolean} isDesktop - Является ли устройство десктопом (для определения качества)
 * @param {string} context - Контекст использования (для определения качества)
 * @returns {string} - Оптимизированный URL
 */
export const getOptimizedImageUrl = (
  originalUrl, 
  size = 'auto', 
  quality = 'auto',
  format = 'auto',
  isDesktop = false,
  context = 'card'
) => {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return originalUrl;
  }

  // Определяем качество автоматически, если не указано
  const finalQuality = quality === 'auto' ? getOptimalQuality(isDesktop, context) : quality;

  // Нормализуем URL
  let url = originalUrl.trim();
  if (!url) return url;

  // Определяем формат
  let finalFormat = format;
  if (format === 'auto') {
    finalFormat = supportsWebP() ? 'webp' : null;
  }

  // Для десктопа на детальных страницах и слайдшоу - используем оригинальное изображение без ресайза
  // Это дает максимальное качество, так как провайдеры предоставляют изображения 1280x720 или выше
  // YouTube maxresdefault обычно 1280x720, но может быть выше для HD видео
  // Vimeo и RuTube обычно 1280x720
  const useOriginalSize = isDesktop && (context === 'detail' || context === 'slideshow' || context === 'fullscreen');
  
  // Если URL уже полный (начинается с http/https), используем как есть
  // Внешние URL (YouTube, Vimeo) уже имеют оптимальное качество от провайдера
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Для максимального качества на десктопе - не ресайзим внешние URL
    // Они уже имеют оптимальное качество от провайдера (обычно 1280x720)
    if (useOriginalSize || size === 0) {
      return url; // Возвращаем оригинальный URL без изменений
    }
    
    // Добавляем query параметры для ресайза на бэкенде (для внутренних URL через proxy)
    if (size !== 'auto' && size > 0) {
      const separator = url.includes('?') ? '&' : '?';
      let params = `w=${size}&q=${finalQuality}`;
      if (finalFormat === 'webp') {
        params += '&f=webp';
      }
      return `${url}${separator}${params}`;
    }
    return url;
  }

  // Если относительный путь, добавляем базовый URL API
  const apiBase = process.env.REACT_APP_API_URL || 'https://api.travmap.org';
  const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
  
  let fullUrl = '';
  if (url.startsWith('/files/')) {
    fullUrl = `${baseUrl}${url}`;
  } else if (url.startsWith('/')) {
    fullUrl = `${baseUrl}${url}`;
  } else if (url.startsWith('avatars/') || url.startsWith('videos/')) {
    fullUrl = `${baseUrl}/files/${url}`;
  } else {
    return url;
  }
  
  // Для максимального качества на десктопе - не ресайзим, только качество и формат
  // ВАЖНО: Если quality=100 и format не указан - возвращаем оригинал без параметров
  // Это позволяет использовать оригинальное изображение без перекодирования
  if (useOriginalSize || size === 0) {
    // Если качество 100% и формат не WebP - возвращаем оригинал без параметров
    if (finalQuality === 100 && finalFormat !== 'webp') {
      return fullUrl; // Оригинальное изображение без обработки
    }
    
    // Если нужен WebP или качество не 100% - добавляем только эти параметры
    const separator = fullUrl.includes('?') ? '&' : '?';
    let params = '';
    if (finalQuality !== 100) {
      params = `q=${finalQuality}`;
    }
    if (finalFormat === 'webp') {
      params += params ? `&f=webp` : `f=webp`;
    }
    return params ? `${fullUrl}${separator}${params}` : fullUrl;
  }
  
  // Добавляем query параметры для ресайза на бэкенде
  if (size !== 'auto' && size > 0) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    let params = `w=${size}&q=${finalQuality}`;
    if (finalFormat === 'webp') {
      params += '&f=webp';
    }
    return `${fullUrl}${separator}${params}`;
  }
  
  return fullUrl;
};

/**
 * Определяет оптимальный размер изображения для контекста
 * 
 * @param {string} context - Контекст использования ('thumbnail', 'card', 'detail', 'fullscreen')
 * @param {string} type - Тип изображения ('avatar' или 'cover')
 * @returns {number} - Размер в пикселях
 */
export const getOptimalSize = (context, type = 'cover') => {
  if (type === 'avatar') {
    switch (context) {
      case 'thumbnail':
      case 'card':
        return IMAGE_SIZES.AVATAR_THUMBNAIL;
      case 'small':
        return IMAGE_SIZES.AVATAR_SMALL;
      case 'medium':
      case 'detail':
        return IMAGE_SIZES.AVATAR_MEDIUM;
      case 'large':
      case 'fullscreen':
        return IMAGE_SIZES.AVATAR_LARGE;
      default:
        return IMAGE_SIZES.AVATAR_THUMBNAIL;
    }
  } else {
    // cover
    switch (context) {
      case 'thumbnail':
      case 'card':
        return IMAGE_SIZES.COVER_THUMBNAIL;
      case 'small':
        return IMAGE_SIZES.COVER_SMALL;
      case 'medium':
      case 'detail':
        return IMAGE_SIZES.COVER_MEDIUM;
      case 'large':
      case 'fullscreen':
        return IMAGE_SIZES.COVER_LARGE;
      default:
        return IMAGE_SIZES.COVER_THUMBNAIL;
    }
  }
};

/**
 * Генерирует srcset для responsive изображений с поддержкой WebP
 * 
 * @param {string} originalUrl - Оригинальный URL
 * @param {Array<number>} sizes - Массив размеров для srcset
 * @param {boolean} isDesktop - Является ли устройство десктопом
 * @param {string} context - Контекст использования
 * @returns {string} - srcset строка
 */
export const generateSrcSet = (
  originalUrl, 
  sizes = [320, 640, 1280, 1920],
  isDesktop = false,
  context = 'card'
) => {
  if (!originalUrl) return '';
  
  return sizes
    .map(size => {
      const quality = context === 'fullscreen' || context === 'slideshow' 
        ? (isDesktop ? 95 : 60) // Снижено с 70 до 60 для мобильных в слайдшоу (оптимизация PageSpeed)
        : (isDesktop ? 85 : 65); // Снижено с 70 до 65 для мобильных в других контекстах
      const url = getOptimizedImageUrl(originalUrl, size, quality, 'auto', isDesktop, context);
      return `${url} ${size}w`;
    })
    .join(', ');
};

/**
 * Генерирует sizes атрибут для responsive изображений
 * 
 * @param {string} context - Контекст использования
 * @returns {string} - sizes строка
 */
export const generateSizes = (context) => {
  switch (context) {
    case 'card':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px';
    case 'detail':
      return '(max-width: 1024px) 100vw, 1280px';
    case 'fullscreen':
      return '100vw';
    default:
      return '100vw';
  }
};

/**
 * Предзагружает изображение
 * 
 * @param {string} url - URL изображения
 * @returns {Promise} - Promise, который резолвится когда изображение загружено
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('URL is required'));
      return;
    }

    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = getOptimizedImageUrl(url);
  });
};

/**
 * Предзагружает несколько изображений
 * 
 * @param {Array<string>} urls - Массив URL изображений
 * @returns {Promise<Array>} - Promise, который резолвится когда все изображения загружены
 */
export const preloadImages = (urls) => {
  return Promise.all(urls.map(url => preloadImage(url).catch(() => null)));
};

/**
 * Проверяет доступность изображения
 * 
 * @param {string} url - URL изображения
 * @returns {Promise<boolean>} - Promise, который резолвится с true если изображение доступно
 */
export const checkImageAvailability = (url) => {
  return new Promise((resolve) => {
    if (!url || typeof url !== 'string') {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = getOptimizedImageUrl(url);
  });
};

/**
 * Получает доступный URL изображения, возвращает fallback если изображение недоступно
 * 
 * @param {string} url - URL изображения
 * @param {string} fallbackUrl - URL для fallback (по умолчанию дефолтная иконка)
 * @returns {Promise<string>} - Promise, который резолвится с доступным URL
 */
export const getAvailableImageUrl = async (url, fallbackUrl = null) => {
  if (!url || typeof url !== 'string') {
    return fallbackUrl || `${process.env.REACT_APP_API_URL || 'https://api.travmap.org'}/icons/icon-512x512.png`;
  }

  const isAvailable = await checkImageAvailability(url);
  if (isAvailable) {
    return url;
  }

  // Если изображение недоступно, возвращаем fallback
  return fallbackUrl || `${process.env.REACT_APP_API_URL || 'https://api.travmap.org'}/icons/icon-512x512.png`;
};
